import * as PolarisIcons from '@shopify/polaris-icons';
import userRoles from '../../utils/userRoles';
import { canAccessPmDashboard } from '../../utils/pmDashboardAccess';

/*
  Pages and actions of the palette. Access mirrors the routes in client/src/index.jsx (ProtectedRoute roles, flags
  and user ids) and the sidebar (shell/navigation.jsx), so nobody is offered a page that sends them back home.
  keywords: other words people use for it (Dutch names of the documents included), all matched by fuzzy.js.
*/

const ADMINS = [userRoles.AdminFull, userRoles.AdminData];
const FULL_ADMIN = [userRoles.AdminFull];

const PAGES = [
  { title: 'Home', url: '/home', icon: PolarisIcons.HomeIcon, keywords: ['dashboard', 'start'] },
  { title: 'My approvals', url: '/approvals', icon: PolarisIcons.CheckCircleIcon, keywords: ['approval queue', 'goedkeuringen', 'approve invoices'] },
  { title: 'Approval intake', url: '/approvals/intake', icon: PolarisIcons.CheckCircleIcon, keywords: ['assign incoming invoices', 'intake queue'], roles: ADMINS },
  { title: 'Companies', url: '/companies', icon: PolarisIcons.InventoryIcon, keywords: ['customers', 'suppliers', 'klanten', 'leveranciers', 'firma', 'bedrijven'] },
  { title: 'Contacts', url: '/contacts', icon: PolarisIcons.PersonIcon, keywords: ['contactpersonen', 'people'] },
  { title: 'Reports', url: '/reports', icon: PolarisIcons.FlagIcon, keywords: ['rapporten', 'export'] },
  { title: 'BOM cost tool', url: '/reports/bom-tool', icon: PolarisIcons.FlagIcon, keywords: ['inventor', 'bill of materials', 'stuklijst'] },
  { title: 'Cabinet price estimator', url: '/reports/cabinet-estimator', icon: PolarisIcons.FlagIcon, keywords: ['elektro tech', 'kast', 'electrical cabinet'] },
  { title: 'Inquiries', url: '/inquiries', icon: PolarisIcons.EmailIcon, keywords: ['prijsaanvragen', 'price requests', 'rfq'] },
  { title: 'Purchase orders', url: '/purchases', icon: PolarisIcons.CartIcon, keywords: ['bestellingen', 'aankoop', 'purchases', 'po'] },
  { title: 'Quotations', url: '/quotations', icon: PolarisIcons.NoteIcon, keywords: ['offertes', 'quotes'] },
  { title: 'Sales orders', url: '/sales', icon: PolarisIcons.ConfettiIcon, keywords: ['orderbevestigingen', 'verkoop', 'sales'] },
  { title: 'Delivery notes', url: '/delivery_notes', icon: PolarisIcons.DeliveryIcon, keywords: ['leveringsbonnen', 'leverbon'] },
  { title: 'Customer map', url: '/customer-map', icon: PolarisIcons.CompassIcon, keywords: ['kaart', 'map'] },
  { title: 'Machine map', url: '/machine-map', icon: PolarisIcons.CompassIcon, keywords: ['kaart', 'map'] },
  { title: 'Invoices OUT', url: '/invoices', icon: PolarisIcons.CashEuroIcon, keywords: ['facturen', 'verkoopfacturen', 'sales invoices'] },
  { title: 'Invoices IN', url: '/invoices_in', icon: PolarisIcons.ReceiptIcon, keywords: ['aankoopfacturen', 'inkomende facturen', 'supplier invoices', 'purchase invoices'] },
  { title: 'Recurring invoices', url: '/recurring_invoices', icon: PolarisIcons.OrderRepeatIcon, keywords: ['subscriptions', 'terugkerende facturen'] },
  { title: 'Credit notes OUT', url: '/creditnotes', icon: PolarisIcons.ReceiptRefundIcon, keywords: ['creditnota'] },
  { title: 'Credit notes IN', url: '/creditnotes_in', icon: PolarisIcons.ReceiptRefundIcon, keywords: ['creditnota inkomend', 'supplier credit notes'] },
  { title: 'Banking', url: '/banking', icon: PolarisIcons.BankIcon, keywords: ['bank', 'ponto', 'payments', 'betalingen', 'transactions'], flag: 'bankingAccess' },
  { title: 'Products', url: '/products', icon: PolarisIcons.ProductIcon, keywords: ['onderdelen', 'artikels', 'parts', 'catalog'] },
  { title: 'Projects', url: '/projects', icon: PolarisIcons.ThemeIcon, keywords: ['projecten'] },
  { title: 'Jobs', url: '/jobs', icon: PolarisIcons.WrenchIcon, keywords: ['interventies', 'werkbonnen', 'service'] },
  { title: 'Montage bons', url: '/montage_bons', icon: PolarisIcons.ClipboardChecklistIcon, keywords: ['montagebonnen', 'service reports'] },
  { title: 'Machines', url: '/machines', icon: PolarisIcons.SettingsIcon, keywords: ['toestellen', 'installed base'] },
  { title: 'Maintenance contracts', url: '/maintenance_contracts', icon: PolarisIcons.ContractIcon, keywords: ['onderhoudscontracten', 'service contracts'] },
  { title: 'Interactions', url: '/interactions', icon: PolarisIcons.AffiliateIcon, keywords: ['activiteiten', 'calls', 'visits', 'notes', 'crm'] },
  { title: 'PM Dashboard', url: '/pm-dashboard', icon: PolarisIcons.ChartVerticalFilledIcon, keywords: ['perfect moose', 'dealers'], check: canAccessPmDashboard },
  { title: 'Stock value', url: '/inventory/value', icon: PolarisIcons.InventoryFilledIcon, keywords: ['inventory', 'voorraad', 'stock'] },
  { title: 'Files', url: '/files', icon: PolarisIcons.FolderIcon, keywords: ['documents', 'sharepoint', 'bestanden'] },
  { title: 'Account', url: '/account', icon: PolarisIcons.PersonIcon, keywords: ['profile', 'my account', 'profiel'] },
  { title: 'Expenses', url: '/expenses', icon: PolarisIcons.ReceiptEuroIcon, keywords: ['onkosten', 'expense claims'], roles: FULL_ADMIN },
  { title: 'Cashbook', url: '/cashbook', icon: PolarisIcons.ReceiptEuroIcon, keywords: ['kasboek', 'cash'], roles: ADMINS },
  { title: 'Technicians payroll', url: '/technicians', icon: PolarisIcons.ShieldCheckMarkIcon, keywords: ['hr', 'payroll', 'loon', 'technicians'], roles: ADMINS },
  { title: 'Holiday admin', url: '/holidays', icon: PolarisIcons.ShieldCheckMarkIcon, keywords: ['hr', 'verlof', 'vakantie', 'holidays'], roles: ADMINS },
  { title: 'Employees', url: '/employees', icon: PolarisIcons.TeamIcon, keywords: ['hr', 'werknemers', 'personeel', 'staff'], roles: ADMINS },
  { title: 'Fleet', url: '/fleet', icon: PolarisIcons.DeliveryFilledIcon, keywords: ['wagenpark', 'cars', 'voertuigen', 'vehicles'], roles: FULL_ADMIN },
  { title: 'Roasthubs', url: '/roasthubs', icon: PolarisIcons.CashEuroIcon, keywords: ['roasthub'], roles: FULL_ADMIN }
];

// Analytics tabs (client/src/pages/analytics/index.jsx): ?tab=<index>
const ANALYTICS_TABS = ['Dashboard', 'Overview', 'Revenue', 'Costs', 'Cashflow', 'Cash planning', 'Yearly report'];

// Settings pages (client/src/pages/settings/index.jsx)
const SETTINGS = [
  ['Industries', '/settings/sectors', ['sectors']],
  ['Contact titles', '/settings/contacttitels', []],
  ['Company tags', '/settings/companytags', ['tags']],
  ['PUB codes', '/settings/pubCodes', []],
  ['Quotation statuses', '/settings/quotationstatuses', []],
  ['Purchase types', '/settings/purchasetypes', ['cost types']],
  ['Product units', '/settings/units', ['eenheden']],
  ['Picking places', '/settings/pickingplaces', ['stock places', 'warehouses']],
  ['Maintenance contract types', '/settings/service_contracttypes', []],
  ['Machine brands', '/settings/machinebrands', ['merken']],
  ['Machine types', '/settings/machinetypes', []],
  ['Agreement types', '/settings/agreement_types', []],
  ['Document types', '/settings/documenttypes', []],
  ['Email formats', '/settings/emailformats', ['templates']],
  ['Payment reminder texts', '/settings/remindertexts', ['rappels', 'aanmaningen']],
  ['Payment conditions', '/settings/paymentconditions', ['betalingstermen']],
  ['VAT shifts', '/settings/vatshifts', ['btw verlegd']],
  ['Invoice statuses', '/settings/invoicestatuses', []],
  ['Payment collection types', '/settings/paymentcollections', ['incasso']],
  ['Overhead percentages', '/settings/overhead_percentages', []],
  ['Business units & assignment rules', '/settings/business_units', ['bu']],
  ['Users', '/settings/users', ['gebruikers', 'accounts']],
  ['User types', '/settings/usertypes', ['roles']],
  ['Hidden companies', '/settings/hidden_companies', ['confidential']],
  ['AI connections', '/settings/ai_connections', ['mcp', 'claude', 'chatgpt']]
];

// Creating documents: the pages that work without a company (shell/shortcuts.js, sidebar secondary actions)
const CREATE = [
  { title: 'New company', url: '/companies/new', icon: PolarisIcons.InventoryIcon, keywords: ['add customer', 'add supplier', 'nieuwe klant'] },
  { title: 'New sales order', url: '/sales/new', icon: PolarisIcons.ConfettiIcon, keywords: ['orderbevestiging', 'add sale'] },
  { title: 'New purchase order', url: '/purchases/new', icon: PolarisIcons.CartIcon, keywords: ['bestelling', 'add purchase'] },
  { title: 'New invoice OUT', url: '/invoices/new', icon: PolarisIcons.CashEuroIcon, keywords: ['factuur', 'add invoice'] },
  { title: 'New invoice IN', url: '/invoices_in/new', icon: PolarisIcons.ReceiptIcon, keywords: ['aankoopfactuur', 'supplier invoice'] },
  { title: 'New credit note IN', url: '/creditnotes_in/new', icon: PolarisIcons.ReceiptRefundIcon, keywords: ['creditnota'] },
  { title: 'New recurring invoice', url: '/recurring_invoices/new', icon: PolarisIcons.OrderRepeatIcon, keywords: ['subscription'] },
  { title: 'New project', url: '/projects/new', icon: PolarisIcons.ThemeIcon, keywords: ['nieuw project'] },
  { title: 'New product', url: '/products/new', icon: PolarisIcons.ProductIcon, keywords: ['onderdeel', 'artikel', 'part'] },
  { title: 'New machine', url: '/machines/new', icon: PolarisIcons.SettingsIcon, keywords: ['toestel'] },
  { title: 'Log an interaction', url: '/interactions?new=true', icon: PolarisIcons.AffiliateIcon, keywords: ['new interaction', 'call', 'visit', 'note', 'activiteit'] }
];

function allowed(entry, user) {
  if (entry.roles && !entry.roles.includes(user?.role)) return false;
  if (entry.flag && !user?.[entry.flag]) return false;
  if (entry.check && !entry.check(user)) return false;
  return true;
}

/*
  Every command the user may run: { id, kind, section, title, subtitle, url | run, icon, keywords }.
  kind: page | create | setting | action. run() gets { logout }.
*/
export function buildCommands(user, { pickingplaces = [] } = {}) {
  if (!user) return [];
  const commands = [];
  for (const page of PAGES) {
    if (allowed(page, user)) commands.push({ id: `page:${page.url}`, kind: 'page', section: 'Go to', subtitle: 'Page', ...page });
  }
  // the stock places of the sidebar's Inventory menu
  for (const place of Array.isArray(pickingplaces) ? pickingplaces : []) {
    if (!place?.counter) continue;
    commands.push({
      id: `page:/inventory/${place.counter}`,
      kind: 'page',
      section: 'Go to',
      title: `Inventory: ${place.pickingplace}`,
      subtitle: 'Stock place',
      url: `/inventory/${place.counter}`,
      icon: PolarisIcons.InventoryFilledIcon,
      keywords: ['stock', 'voorraad', 'inventory']
    });
  }
  if (user.role === userRoles.AdminFull) {
    ANALYTICS_TABS.forEach((tab, index) =>
      commands.push({
        id: `page:/analytics?tab=${index}`,
        kind: 'page',
        section: 'Go to',
        title: index === 0 ? 'Analytics' : `Analytics: ${tab}`,
        subtitle: 'Analytics',
        url: `/analytics?tab=${index}`,
        icon: PolarisIcons.ChartVerticalFilledIcon,
        keywords: ['analytics', 'kpi', 'metrics', tab.toLowerCase()]
      })
    );
    commands.push({
      id: 'page:/settings',
      kind: 'setting',
      section: 'Settings',
      title: 'Settings',
      subtitle: 'Settings',
      url: '/settings',
      icon: PolarisIcons.SettingsIcon,
      keywords: ['instellingen', 'configuration']
    });
    for (const [title, url, keywords] of SETTINGS) {
      commands.push({
        id: `setting:${url}`,
        kind: 'setting',
        section: 'Settings',
        title,
        subtitle: 'Settings',
        url,
        icon: PolarisIcons.SettingsIcon,
        keywords: ['settings', 'instellingen', ...keywords]
      });
    }
  }
  for (const create of CREATE) {
    commands.push({ id: `create:${create.url}`, kind: 'create', section: 'Create', subtitle: 'Create', ...create });
  }
  commands.push({
    id: 'action:logout',
    kind: 'action',
    section: 'Account',
    title: 'Log out',
    subtitle: 'Account',
    icon: PolarisIcons.ExitIcon,
    keywords: ['sign out', 'afmelden', 'uitloggen'],
    run: ({ logout }) => logout()
  });
  return commands;
}

// shown when the palette opens without a query
export const QUICK_CREATE_URLS = ['/companies/new', '/sales/new', '/purchases/new', '/invoices/new', '/projects/new', '/interactions?new=true'];
export const QUICK_PAGE_URLS = ['/approvals', '/companies', '/quotations', '/sales', '/purchases', '/invoices', '/invoices_in', '/products', '/projects', '/jobs'];
